import * as Sentry from '@sentry/react';
import { Frown } from 'lucide-react';
import { Component, ReactNode } from 'react';

import { Button } from './ui/Button';

import { ROUTE_PATHS } from '@/config/constants';
import i18n from '@/lib/i18n';

interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    Sentry.captureException(error);
  }

  handleTryAgain = () => {
    window.location.reload();
  };

  handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  };

  handleReset = () => {
    this.setState((state) => ({ ...state, hasError: false }));
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-gray-100">
          <div className="px-4 text-center">
            <div className="gap- mb-2 flex items-center justify-center gap-3 text-red-600">
              <h1 className="text-4xl font-bold">
                {i18n.t('components.ErrorBoundary.title')}
              </h1>
              <Frown size={30} />
            </div>

            <p className="mb-8 text-xl text-gray-600">
              {i18n.t('components.ErrorBoundary.description')}
            </p>

            <div className="space-x-2">
              <Button onClick={this.handleTryAgain}>
                {i18n.t('components.ErrorBoundary.tryAgain')}
              </Button>

              <Button variant="outline" onClick={this.handleReset} asChild>
                <a href={ROUTE_PATHS.HOME}>
                  {i18n.t('components.ErrorBoundary.reset')}
                </a>
              </Button>

              <Button variant="destructive" onClick={this.handleLogout}>
                {i18n.t('components.ErrorBoundary.logout')}
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
